import { ComponentProps, FC } from "react";

const DEFAULT_SELECTOR_PREFIX = ".react-entrypoint";

/**
 * Union of the required (non-optional) keys of `T`. Optional keys are dropped via
 * `as` key remapping so the required-props match array only has to list mandatory props.
 */
type OnlyRequired<T> = keyof {
  [P in keyof T as undefined extends T[P] ? never : P]: T[P];
};

/**
 * View loader hold list of app components loaded into page. These components are then subscribed by app.
 * Implements Observer pattern
 */

export interface ViewComponentDefinition<T = any> {
  /**
   * Css selector for use with querySelectorAll() method
   */
  selector: string;

  /**
   * React function component for render
   */
  component: FC<T>;

  /**
   * Id of component
   */
  id?: string;

  /**
   * Required props for component, checked at initiation
   */
  requiredProps?: readonly string[];
}
export type ViewComponentHandler = (components: Required<ViewComponentDefinition>[]) => void;

const components: Required<ViewComponentDefinition>[] = [];
const handlers: ViewComponentHandler[] = [];

/**
 * Call subscribed handlers of components loader
 */
function publishComponents() {
  handlers.forEach((item) => {
    item.call(null, components);
  });
}

/**
 * Register component for loader
 */
export function registerComponent<T>(component: ViewComponentDefinition<T>) {
  components.push({
    ...component,
    requiredProps: component.requiredProps || [],
    id: `app-component-${components.length}`,
  });
  publishComponents();
}

/**
 * Register handler for loading components
 */
export function subscribeComponents(fn: ViewComponentHandler) {
  handlers.push(fn);
  publishComponents();
}

type AllMembersPresent<
  Union extends OnlyRequired<ComponentProps<any>>,
  Arr extends readonly OnlyRequired<ComponentProps<any>>[],
> = Exclude<Union, Arr[number]> extends never ? true : false;

// Better approach - use a validation function:
function createMatchArray<T extends OnlyRequired<ComponentProps<any>>>() {
  return <A extends readonly T[]>(arr: AllMembersPresent<T, A> extends true ? A : never): A => arr;
}

export function loadViewComponent<TViewProps extends ComponentProps<any> = ComponentProps<any>>(
  /**
   * Component to load
   */
  component: FC<TViewProps>,
  /**
   * CSS Selector to load component into
   */
  selector?: string,
  /**
   * Array of required props to be present in the component's props
   */
  requiredProps?: (matcher: ReturnType<typeof createMatchArray<OnlyRequired<TViewProps>>>) => readonly string[]
): void;

/*
 * Register app component to load. Component is rendered immediately after runtime load or registration of the component
 */
export function loadViewComponent<TViewProps extends ComponentProps<any> = ComponentProps<any>>(
  component: FC<TViewProps>,
  selector?: string,
  requiredProps?: (matcher: ReturnType<typeof createMatchArray<OnlyRequired<TViewProps>>>) => readonly string[]
) {
  if (import.meta.env.MODE === "development") {
    // eslint-disable-next-line no-console
    console.info(
      `Registering component "${component.name}" with selector "${
        selector || `${DEFAULT_SELECTOR_PREFIX}-${component.name}`
      }"`
    );
  }
  registerComponent<TViewProps>({
    selector: selector || `${DEFAULT_SELECTOR_PREFIX}-${component.name}`,
    component,
    requiredProps: requiredProps ? requiredProps(createMatchArray<OnlyRequired<TViewProps>>()) : [],
  });
}
